// FLOW-06 (Task 6.0) — como uma execução de flow aparece na tela, igual nas telas da aba PowerFlow
// (a lista de Flows, a de Execuções e o detalhe): o glifo e o nome do estado, quem chamou com nome
// (PLAN G24), há quanto tempo e quanto durou. As telas só leem: nada aqui pede nada ao servidor.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { generateRelativeTime } from 'shared/helpers/DateHelper';

// Os seis estados (os nomes fixos da SPEC), com os glifos da aba Execuções do editor
// (`execucoesDoFlow.js`, `GLIFOS`): a cor fica no glifo, e o texto ao lado é neutro.
export const GLIFOS_DOS_ESTADOS = {
  enfileirada: 'i-lucide-clock-3 text-n-slate-10',
  rodando:
    'i-lucide-loader-circle animate-spin motion-reduce:animate-none text-n-slate-10',
  sucesso: 'i-lucide-check text-n-teal-9',
  erro: 'i-lucide-x text-n-ruby-9',
  cancelada: 'i-lucide-square text-n-slate-10',
  interrompida: 'i-lucide-triangle-alert text-n-amber-11',
};

// Quem chamou. O ícone é neutro: diz a porta, e não o estado. A ordem é a do filtro "Quem chamou" da
// lista, com os dois agentes juntos. FLOW-03a (SPEC S7): a agenda e o webhook entram no fim, com os
// ícones dos gatilhos (`gatilhosNaTela.js`).
export const ICONES_DE_QUEM = {
  poweragents: 'i-lucide-bot',
  runner: 'i-lucide-bot',
  manual: 'i-lucide-mouse-pointer-click',
  api: 'i-lucide-code-xml',
  mcp: 'i-lucide-sparkles',
  agenda: 'i-lucide-calendar-clock',
  webhook: 'i-lucide-webhook',
};

// Os agentes: o PowerAgents e o agent-runner antigo (`runner`), que ainda chama o `/tools/invoke` em
// produção. Os dois aparecem do mesmo jeito: pelo nome do agente, ou pelo `agent_ref` sem ele.
const INVOCADORES_DE_AGENTE = ['poweragents', 'runner'];
export const doAgente = execucao =>
  INVOCADORES_DE_AGENTE.includes(execucao.invocador);

export function useExecucaoNaTela() {
  const { t, locale } = useI18n();
  const idioma = computed(() => String(locale.value || 'en').replace('_', '-'));

  const rotulosDosEstados = computed(() => ({
    enfileirada: t('POWER_TOOLS.POWERFLOW.STATES.ENFILEIRADA'),
    rodando: t('POWER_TOOLS.POWERFLOW.STATES.RODANDO'),
    sucesso: t('POWER_TOOLS.POWERFLOW.STATES.SUCESSO'),
    erro: t('POWER_TOOLS.POWERFLOW.STATES.ERRO'),
    cancelada: t('POWER_TOOLS.POWERFLOW.STATES.CANCELADA'),
    interrompida: t('POWER_TOOLS.POWERFLOW.STATES.INTERROMPIDA'),
  }));

  // G24: "Ana · manual", "API · Ana", "MCP · Claude de Ana" (o `cliente` é o aplicativo OAuth; sem
  // ele, "MCP · Ana") e "Agente Sayuri". FLOW-03a: "Agenda · varredura" e "Webhook · omnichat". No agente (o PowerAgents ou o agent-runner), o PowerTools
  // grava o `agent_ref` e o Rails completa o `nome` do PowerAgent; sem o nome, vale o `agent_ref`.
  function quemChamou(execucao) {
    const quem = execucao.quem || {};
    if (doAgente(execucao)) {
      return t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.POWERAGENTS', {
        nome: quem.nome || quem.agent_ref || '',
      });
    }
    const nome = quem.nome || '';
    if (execucao.invocador === 'manual') {
      return t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.MANUAL', { nome });
    }
    if (execucao.invocador === 'api') {
      return t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.API', { nome });
    }
    if (execucao.invocador === 'mcp') {
      return quem.cliente
        ? t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.MCP_CLIENT', {
            cliente: quem.cliente,
            nome,
          })
        : t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.MCP', { nome });
    }
    // FLOW-03a (SPEC S7; PLAN P12): o gatilho pelo `id` dele no YAML, "Agenda · varredura" e
    // "Webhook · omnichat". O `quem` é `{tipo: 'gatilho', gatilho_id, flow}`.
    if (execucao.invocador === 'agenda') {
      return t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.SCHEDULE', {
        gatilho: quem.gatilho_id || '',
      });
    }
    if (execucao.invocador === 'webhook') {
      return t('POWER_TOOLS.POWERFLOW.EXECUTIONS.WHO.WEBHOOK', {
        gatilho: quem.gatilho_id || '',
      });
    }
    return nome || execucao.invocador;
  }

  // "há 40 segundos", "há 12 minutos", "ontem": o `Intl` na língua da conta.
  function haQuanto(instante) {
    const em = Date.parse(instante);
    if (Number.isNaN(em)) return '';
    const s = Math.max(0, Math.round((Date.now() - em) / 1000));
    if (s < 60) return generateRelativeTime(-s, 'second', locale.value);
    if (s < 3600) {
      return generateRelativeTime(-Math.floor(s / 60), 'minute', locale.value);
    }
    if (s < 86400) {
      return generateRelativeTime(-Math.floor(s / 3600), 'hour', locale.value);
    }
    return generateRelativeTime(-Math.floor(s / 86400), 'day', locale.value);
  }

  // "412 ms", "3,8 s", "1 min 4 s". Sem duração (a que ainda roda), nada: o estado já diz.
  const formatoDosSegundos = computed(
    () => new Intl.NumberFormat(idioma.value, { maximumFractionDigits: 1 })
  );
  function duracao(ms) {
    if (ms === null || ms === undefined) return '';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    if (ms < 60000) return `${formatoDosSegundos.value.format(ms / 1000)} s`;
    const minutos = Math.floor(ms / 60000);
    const segundos = Math.round((ms % 60000) / 1000);
    return segundos ? `${minutos} min ${segundos} s` : `${minutos} min`;
  }

  return { idioma, rotulosDosEstados, quemChamou, haQuanto, duracao };
}
